/**
 * Order API Service
 * API calls for order management and payment processing
 */

import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth token
function getAuthToken(): string {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found. Please login again.');
  }
  return token;
}

// Helper function to get auth headers
function getAuthHeaders(): Record<string, string> {
  return {
    'Authorization': `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json',
  };
}

export interface CreateOrderRequest {
  property_id: number;
  duration_months: number;
  start_date: string; // ISO date string
  end_date: string; // ISO date string
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
}

export interface OrderResponse {
  id: number;
  order_code: string;
  customer_id: number;
  property_id: number;
  landlord_id: number;
  duration_months: number;
  start_date: string;
  end_date: string;
  monthly_price: number;
  total_rental_amount: number;
  deposit_amount: number;
  commission_amount: number;
  platform_fee: number;
  total_amount: number;
  total_paid?: number;  // Actual amount paid from payment transactions
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  payment_method?: string;
  payment_status: string;
  status: string;
  created_at: string;
  updated_at: string;
  property?: {
    id: number;
    title: string;
    type: string;
    location: string;
    price: number;
    images: string[];
    bedrooms?: number;
    bathrooms?: number;
    area?: number;
    payment_term?: string;
    min_duration?: string;
    owner?: {
      id: number;
      name: string;
      avatar?: string;
      verified: boolean;
    };
  };
}

export interface UpdateOrderRequest {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_address?: string;
  duration_months?: number;
  start_date?: string;
  end_date?: string;
}

export interface CreatePaymentTransactionRequest {
  order_id: number;
  payment_method: string;
  amount: number;
}

export interface PaymentTransactionResponse {
  id: number;
  transaction_code: string;
  order_id: number;
  amount: number;
  payment_method: string;
  status: string;
  created_at: string;
}

export const orderApi = {
  /**
   * Create a new order
   */
  async createOrder(orderData: CreateOrderRequest): Promise<OrderResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orders`,
        orderData,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating order:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể tạo đơn hàng. Vui lòng thử lại.'
      );
    }
  },

  /**
   * Get all orders for current user
   */
  async getOrders(params?: {
    status?: string;
    payment_status?: string;
    limit?: number;
    offset?: number;
  }): Promise<OrderResponse[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.payment_status) queryParams.append('payment_status', params.payment_status);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      
      const url = `${API_BASE_URL}/orders${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return response.data;
    } catch (error: any) {
      console.error('Error loading orders:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể tải danh sách đơn hàng. Vui lòng thử lại.'
      );
    }
  },

  /**
   * Get order by ID
   */
  async getOrder(orderId: number | string): Promise<OrderResponse> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/orders/${orderId}`,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error loading order:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể tải đơn hàng. Vui lòng thử lại.'
      );
    }
  },

  /**
   * Update order
   */
  async updateOrder(orderId: number | string, updates: UpdateOrderRequest): Promise<OrderResponse> {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/orders/${orderId}`,
        updates,
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error updating order:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể cập nhật đơn hàng. Vui lòng thử lại.'
      );
    }
  },

  /**
   * Cancel order
   */
  async cancelOrder(orderId: number | string): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/orders/${orderId}`,
        { headers: getAuthHeaders() }
      );
    } catch (error: any) {
      console.error('Error cancelling order:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể hủy đơn hàng. Vui lòng thử lại.'
      );
    }
  },

  /**
   * Create payment transaction
   */
  async createPaymentTransaction(
    transactionData: CreatePaymentTransactionRequest
  ): Promise<PaymentTransactionResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/orders/${transactionData.order_id}/payments`,
        {
          payment_method: transactionData.payment_method,
          amount: transactionData.amount,
        },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error creating payment transaction:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể tạo giao dịch thanh toán. Vui lòng thử lại.'
      );
    }
  },



  /**
   * Get property details for order calculation
   * This is used to get deposit and commission info
   */
  async getPropertyForOrder(propertyId: number): Promise<{
    id: number;
    price: number;
    deposit: string | null;
    commission: number | null;
    commission_enabled: boolean;
    owner_id: number;
    min_duration?: string | null;
  }> {
    try {
      // Use existing property API
      const response = await axios.get(
        `${API_BASE_URL}/properties/${propertyId}`,
        { headers: getAuthHeaders() }
      );
      const property = response.data.data || response.data;
      // Parse min_duration from property (could be string like "3" or number)
      if (property.min_duration) {
        const parsed = parseInt(property.min_duration);
        if (!isNaN(parsed) && parsed > 0) {
          // minDuration would be parsed
        }
      }
      
      return {
        id: property.id,
        price: property.price,
        deposit: property.deposit || null,
        commission: property.commission || null,
        commission_enabled: property.commission_enabled || false,
        owner_id: property.owner_id || property.owner?.id,
        min_duration: property.min_duration || null,
      };
    } catch (error: any) {
      console.error('Error loading property for order:', error);
      throw new Error(
        error.response?.data?.detail || 
        error.response?.data?.message || 
        'Không thể tải thông tin bất động sản. Vui lòng thử lại.'
      );
    }
  },
};

